export interface PhotoPrivacySection {
  title: string;
  content: string | string[];
  subsections?: PhotoPrivacySection[];
}

export interface PhotoPrivacyContent {
  title: string;
  introduction: string;
  sections: PhotoPrivacySection[];
  contactInfo: {
    email: string;
  };
}

export const PHOTO_PRIVACY_CONTENT: PhotoPrivacyContent = {
  title: "Photo & Image Privacy Policy",
  introduction: "Before using Shaku's photo measurement feature, please review the following terms. By continuing, you confirm that you understand and agree to them.",
  sections: [
    {
      title: "Age Requirement",
      content: [
        "You must be 18 years or older to use Shaku's photo measurement tools.",
        "If you are under 18, you may only use this feature with the consent and supervision of a parent or legal guardian.",
      ],
    },
    {
      title: "Face Blurring & Image Processing",
      content: [
        "To protect your privacy:",
        "• Your face is automatically blurred on your device before any image is uploaded.",
        "• Shaku never stores or processes unblurred facial images.",
        "• Only blurred and non-identifiable measurement data is sent to our servers.",
      ],
    },
    {
      title: "Data Storage, Security & Encryption",
      content: [
        "• All images and measurement data stored on our servers are encrypted in transit and at rest.",
        "• Your data is used only to provide size recommendations, improve the app experience, and for internal analytics in anonymized form.",
        "• We never sell your images or personal information to third parties.",
      ],
    },
    {
      title: "Right to Delete Your Data",
      content: [
        "You may delete your photos, measurements, and account data at any time by:",
        "• Using the Delete Account option in the app, or",
        "• Contacting us at support@shaku.tech",
        "",
        "Once your account is deleted:",
        "• All stored images and personal data will be permanently erased,",
        "• Except where limited retention is required by law (e.g., fraud prevention or security logs).",
      ],
    },
    {
      title: "Compliance with GDPR & PIPEDA",
      content: [
        "Shaku complies with:",
        "",
        "GDPR (General Data Protection Regulation)",
        "Which includes your rights to:",
        "• Access your data",
        "• Request correction",
        "• Request deletion",
        "• Withdraw consent",
        "• Request data portability",
        "• File a complaint with your supervisory authority",
        "",
        "PIPEDA (Canada's Privacy Law)",
        "Which requires:",
        "• Your informed consent before data collection",
        "• Clear explanation of how your data is used",
        "• Secure storage and handling",
        "• Access to your data upon request",
        "• The right to challenge the accuracy and handling of your information",
        "",
        "We also comply with provincial privacy laws where applicable (e.g., Quebec's Law 25).",
      ],
    },
    {
      title: "How Your Data Is Used",
      content: [
        "Your photos and measurements are used for:",
        "• Generating size recommendations",
        "• Improving accuracy of our AI models (in anonymized or aggregated form only)",
        "• Diagnosing issues and enhancing system performance",
        "",
        "Images are never shared with retailers unless you explicitly authorize it.",
      ],
    },
    {
      title: "Withdrawal of Consent",
      content:
        "You may withdraw consent for photo processing at any time by disabling camera access or deleting your account. If consent is withdrawn, you may not be able to use certain features of Shaku.",
    },
    {
      title: "Contact Information",
      content: "If you have any privacy concerns or questions, you may contact our Data Protection Officer at:",
    },
  ],
  contactInfo: {
    email: "support@shaku.tech",
  },
};

// Short version for modal
export const PHOTO_PRIVACY_SHORT_CONTENT = {
  title: "Photo & Image Privacy Policy",
  introduction: "To use Shaku's photo measurement feature, please read and accept:",
  points: [
    "You must be 18+, or have a parent/guardian's consent.",
    "Your face is blurred on your device before your photo is uploaded.",
    "Your data and images are encrypted and securely stored.",
    "You may delete all your photos and data at any time by deleting your account.",
    "We comply with GDPR, PIPEDA, and applicable provincial privacy laws.",
  ],
  confirmText: "I confirm and agree to the Photo Capture Terms & Privacy Notice",
  contactEmail: "support@shaku.tech",
};

export const getPhotoPrivacyPlainText = (): string => {
  let text = `${PHOTO_PRIVACY_CONTENT.title}\n\n`;
  text += `${PHOTO_PRIVACY_CONTENT.introduction}\n\n`;

  PHOTO_PRIVACY_CONTENT.sections.forEach((section) => {
    text += `${section.title}\n`;
    if (Array.isArray(section.content)) {
      section.content.forEach((line) => {
        text += `${line}\n`;
      });
    } else if (section.content) {
      text += `${section.content}\n`;
    }

    if (section.subsections) {
      section.subsections.forEach((sub) => {
        text += `\n${sub.title}\n`;
        if (Array.isArray(sub.content)) {
          sub.content.forEach((line) => {
            text += `${line}\n`;
          });
        } else {
          text += `${sub.content}\n`;
        }
      });
    }
    text += "\n";
  });

  text += `\nIf you have questions, contact us at: ${PHOTO_PRIVACY_CONTENT.contactInfo.email}`;

  return text;
};
